import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { fetchEmpresaResumen } from "@/api/reportes";
import ComentariosPanel from "@/components/financiero/ComentariosPanel";

const money = (v) =>
    `$${Number(v || 0).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

async function fetchFechaCorte() {
    try {
        const response = await axios.get(API_ROUTES.fechaCorte);
        return response.data;
    } catch (error) {
        console.error('Error al obtener fecha de corte:', error);
        const ayer = new Date();
        ayer.setDate(ayer.getDate() - 1);
        return {
            fecha_corte: format(ayer, 'yyyy-MM-dd'),
            tiene_datos: false
        };
    }
}

export default function ResumenEmpresaPage({ adminEmpresaId: adminPropId = null }) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Estado para fecha de corte desde backend
    const [fechaCorteData, setFechaCorteData] = useState(null);
    const [fechaCorteLoading, setFechaCorteLoading] = useState(true);

    // Cargar fecha de corte desde backend
    useEffect(() => {
        let mounted = true;

        async function loadFechaCorte() {
            setFechaCorteLoading(true);
            const data = await fetchFechaCorte();
            if (mounted) {
                setFechaCorteData(data);
                setFechaCorteLoading(false);
            }
        }

        loadFechaCorte();

        return () => {
            mounted = false;
        };
    }, []);

    // Fecha formateada para mostrar en la UI
    const fechaCorte = useMemo(() => {
        if (!fechaCorteData?.fecha_corte) return '...';

        try {
            const fecha = parseISO(fechaCorteData.fecha_corte);
            return format(fecha, "dd-MM-yyyy", { locale: es });
        } catch (error) {
            console.error('Error al formatear fecha:', error);
            return fechaCorteData.fecha_corte;
        }
    }, [fechaCorteData]);

    // preferir prop si pasa, luego state, luego param
    const adminEmpresaId =
        adminPropId ??
        location.state?.adminEmpresaId ??
        (params.empresaId ? Number(params.empresaId) : null);

    const [loading, setLoading] = useState(true);
    const [resumen, setResumen] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);

                // usa sesión o admin según exista adminEmpresaId
                const data = await fetchEmpresaResumen({ adminEmpresaId });

                // -------- Lectura cruda de backend --------
                const credito_base = Number(data?.credito_base || 0);
                const credito_usado_raw = Number(data?.credito_usado || 0);
                const extraTemporal = Number(data?.extras_activos?.temporal || 0);
                const extraPromo = Number(data?.extras_activos?.promocional || 0);

                const deposito_total = Number(data?.deposito_total ?? data?.debito_total ?? 0);
                const cargos_total = Number(data?.cargos_total ?? 0);

                const hasDeposit = deposito_total > 0.0001;
                const hasCreditLimit = credito_base > 0 || extraTemporal > 0;

                // Depósito disponible - SIEMPRE usar el que manda el backend
                const depositoDisponibleRaw = Number.isFinite(Number(data?.balance_deposito))
                    ? Number(data.balance_deposito)
                    : deposito_total - cargos_total;

                // Para la UI de "Depósito disponible" nunca mostramos negativo
                const depositoDisponible = Math.max(0, depositoDisponibleRaw);

                // ====== Lógica de CRÉDITO ======
                // REGLA:
                //  - Si hay límite de crédito → usamos credito_usado_raw.
                //  - Si NO hay depósito pero SÍ hay cargos → también tratamos todo como crédito (deuda).
                //  - Si hay depósito y NO hay límite de crédito → modelo sólo depósito, crédito en 0.
                let credit_used;
                let current_credit_limit;

                if (hasCreditLimit || (!hasDeposit && cargos_total > 0)) {
                    // Cuenta con crédito (o deuda pura sin depósitos)
                    credit_used = credito_usado_raw;
                    current_credit_limit = credito_base + extraTemporal - credito_usado_raw;
                } else {
                    // Solo depósito / prepago, no mostramos crédito
                    credit_used = 0;
                    current_credit_limit = 0;
                }

                // ====== BALANCE ======
                // Si hay depósito: balance = depositoDisponibleRaw (puede ser positivo o negativo)
                // Si NO hay depósito pero sí cargos: balance = -credit_used (deuda)
                let balance;

                if (hasDeposit) {
                    // Modelo con depósito: el balance es lo que queda del depósito
                    balance = depositoDisponibleRaw;
                } else if (cargos_total > 0) {
                    // No hay depósito pero sí cargos: es pura deuda
                    balance = -credit_used;
                } else {
                    // No hay ni depósito ni cargos
                    balance = 0;
                }

                // ====== Clientes ======
                const clientes = Array.isArray(data?.clientes)
                    ? data.clientes.map((c) => ({
                        id: c.id ?? c.cliente_id,
                        nombre: c.nombre ?? c.nombre_cliente,
                        deposito_asignado: Number(c.deposito_asignado || 0),
                    }))
                    : [];

                const empresa_id = Number(data?.empresa_id || 0);
                const empresa_nombre =
                    data?.empresa_nombre || data?.empresa?.nombre || data?.nombre_empresa;

                setResumen({
                    empresa_id,
                    empresa_nombre,

                    // CRÉDITO
                    base_limit: credito_base,
                    credit_used,
                    temp_extra: extraTemporal,
                    promo_extra: extraPromo,
                    current_credit_limit,

                    // DÉBITO
                    deposito_total,
                    cargos_total,
                    balance_deposito: depositoDisponible,
                    balance_deposito_raw: depositoDisponibleRaw,

                    // BALANCE (con signo correcto)
                    balance,

                    // detalle
                    clientes,
                });
            } catch (e) {
                console.error(e);
                setError("No se pudo obtener el resumen de la empresa.");
            } finally {
                setLoading(false);
            }
        })();
    }, [adminEmpresaId]);

    if (loading) return <div className="p-4 text-sm text-slate-500">Cargando…</div>;
    if (error) return <div className="p-4 text-sm text-red-600">{error}</div>;
    if (!resumen) return <div className="p-4 text-sm text-slate-500">Sin datos.</div>;

    return (
        <div className="max-w-6xl mx-auto p-4 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-slate-800">Resumen</h1>
                    {resumen.empresa_nombre && (
                        <p className="text-xs text-slate-500">{resumen.empresa_nombre}</p>
                    )}
                    {adminEmpresaId && (
                        <span className="mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                            Modo admin — Empresa #{adminEmpresaId}
                        </span>
                    )}
                </div>
                <button
                    className="px-3 py-1.5 rounded-md border text-xs hover:bg-slate-50"
                    onClick={() => navigate(-1)}
                >
                    ← Volver
                </button>
            </div>

            {/* tarjetas */}
            <div className="grid md:grid-cols-3 gap-3">
                {/* Crédito */}
                <div className="rounded-md border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 p-3">
                    <div className="text-[10px] font-bold uppercase text-green-700 mb-1 tracking-wider">
                        Crédito
                    </div>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span>Límite de crédito</span>
                            <strong>{money(resumen.base_limit)}</strong>
                        </div>
                        <div className="flex justify-between">
                            <span>Crédito usado</span>
                            <strong className="text-rose-700">{money(resumen.credit_used)}</strong>
                        </div>
                        {resumen.temp_extra > 0 && (
                            <div className="flex justify-between bg-indigo-50 px-2 py-1 rounded">
                                <span>Extra temporal</span>
                                <strong>{money(resumen.temp_extra)}</strong>
                            </div>
                        )}
                        <div className="flex justify-between pt-1 border-t">
                            <span>Crédito disponible</span>
                            <strong
                                className={resumen.current_credit_limit >= 0 ? "text-green-800" : "text-red-800"}
                                title={
                                    resumen.current_credit_limit < 0
                                        ? "Sobregiro: el crédito disponible es negativo"
                                        : ""
                                }
                            >
                                {money(resumen.current_credit_limit)}
                            </strong>
                        </div>
                    </div>
                </div>

                {/* Débito */}
                <div className="rounded-md border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-cyan-50 p-3">
                    <div className="text-[10px] font-bold uppercase text-blue-700 mb-1 tracking-wider">
                        Débito
                    </div>
                    <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                            <span>Depósitos totales</span>
                            <strong>{money(resumen.deposito_total)}</strong>
                        </div>
                        <div className="flex justify-between">
                            <span>Cargos</span>
                            <strong className="text-orange-700">{money(resumen.cargos_total)}</strong>
                        </div>
                        <div className="flex justify-between">
                            <span>Saldo disponible</span>
                            <strong className="text-slate-800">
                                {money(resumen.balance_deposito)}
                            </strong>
                        </div>

                        {/* Aviso si el "crudo" fue negativo */}
                        {(resumen.balance_deposito_raw ?? 0) < 0 && (
                            <div className="text-[11px] text-red-600 bg-red-100 px-2 py-1 rounded">
                                ⚠️ Sin depósito: los cargos se cubren contra crédito.
                            </div>
                        )}
                    </div>
                </div>

                {/* Balance */}
                <div
                    className={`rounded-md border-2 p-3 ${resumen.balance >= 0
                        ? "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50"
                        : "border-red-200 bg-gradient-to-br from-red-50 to-pink-50"
                        }`}
                >
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-base">{resumen.balance >= 0 ? "📈" : "📉"}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-blue-700">Balance</span>
                        <span className="text-[10px] text-slate-500 italic normal-case">al {fechaCorte}</span>
                    </div>
                    <div
                        className={`text-xl font-bold ${resumen.balance >= 0 ? "text-emerald-800" : "text-red-800"}`}
                    >
                        {money(resumen.balance)}
                    </div>
                    {resumen.balance < 0 && (
                        <div className="mt-2 text-[11px] text-red-600 bg-red-100 px-2 py-1 rounded flex items-center gap-1">
                            {/* <span>⚠️</span>
                            <span>Nos debe (sin depósito y con crédito sobregirado)</span> */}
                        </div>
                    )}
                </div>
            </div>

            {/* Movimientos */}
            <div className="space-y-2">
                <div className="text-[10px] font-semibold uppercase text-slate-500">Comentarios</div>
                <div className="rounded-md border bg-white">
                    <ComentariosPanel
                        empresa={{ empresa_id: resumen.empresa_id, empresa_nombre: resumen.empresa_nombre }}
                        adminEmpresaId={adminEmpresaId}
                        // pasar totales para mostrar chips en el panel
                        totales={{
                            deposito_total: resumen.deposito_total,
                            cargos_total: resumen.cargos_total,
                            balance_deposito: resumen.balance_deposito,
                            balance_deposito_raw: resumen.balance_deposito_raw
                        }}
                    />
                </div>
            </div>
        </div>
    );
}